'use client';

import dynamic from 'next/dynamic';

const ModalProductDetail = dynamic(
  () =>
    import('@/components/sections/builder/elements/body/modal-product-detail').then(
      (mod) => mod.default
    ),
  { ssr: false }
);

const CartPopup = dynamic(
  () =>
    import('@/components/sections/builder/elements/header/header/cart-dropdown').then(
      (mod) => mod.CartPopup
    ),
  { ssr: false }
);

import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import { ProductListElementDataType } from '@/components/sections/builder/elements/body/product-grid/type';
import { DEFAULT_THEME_PRODUCT_CARD } from '@/components/sections/builder/theme/product-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useGetProductDetail } from '@/hooks/api/storefront/product-detail';
import useModal from '@/hooks/useModal';
import { addToCart } from '@/lib/actions/cart/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import { cn, formatIDR, truncateText } from '@/lib/utils';
import { ElementType, ThemeType, useBuilderStore } from '@/stores/builder';
import { useCartProduct } from '@/stores/cart';
import { StoreProduct } from '@/types/http';
import { BaseCart } from '@/types/http/cart/common';
import { IProduct } from '@/types/product';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import Cookies from 'js-cookie';
import DynamicImage from '@/components/commons/dynamic-image';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { AxiosError } from 'axios';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { CollectionTabElementDataType } from '@/components/sections/builder/elements/body/collection-tab/type';
import PriceBookIcon from '@/components/icons/price-book-icon';

type CardProductType = {
  data: ProductGridElementDataType | ProductListElementDataType | CollectionTabElementDataType;
  product: StoreProduct;
  element: ElementType;
  preview?: boolean;
  theme?: ThemeType;
  themeId?: string;
};

export default function CardProductBuilder({
  data,
  product,
  element,
  preview,
  theme,
}: CardProductType) {
  const firstImage = product.product_assets[0];
  const secondImage = product.product_assets[1];
  const [isLoading, setLoading] = React.useState(false);
  const [cartPayload, setCartPayload] = React.useState<BaseCart | null>(null);
  const popupDrawer = useModal();

  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const customerToken = Cookies.get('accessToken');
  const host = window.location.host;

  const productDetailModal = useModal<Partial<IProduct>>();

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const themeSetting = preview ? builder.theme : theme;

  const cartProduct = useCartProduct(
    (state) => ({
      setQuantity: state.setQuantity,
      setVariant: state.setVariant,
      item_id: state.item_id,
      sku: state.sku,
      sell_price: state.sell_price,
      variants: state.variants,
      quantity: state.quantity,
      clear: state.clear,
      item_weight: state.item_weight,
      item_height: state.item_height,
      item_width: state.item_width,
      item_length: state.item_length,
    }),
    shallow
  );

  const discountPercentage = useMemo(() => {
    if ((product?.discount_price as number) > 0) {
      return product?.discount_price !== undefined
        ? product?.sell_price !== undefined
          ? (((((product.sell_price as number) - product?.discount_price) as number) /
              product.sell_price) as number) * 100
          : 0
        : 0;
    }
    return 0;
  }, [product, product.sell_price, product.discount_price]);

  const mutateProduct = useGetProductDetail(customerToken as string, {
    gcTime: 0,
  });

  const onClickChooseOptions = async () => {
    if (!productDetailModal.isShow) {
      productDetailModal.open();
    }
  };

  const isSoldOut = useMemo(() => {
    if (product.available_qty === 0 || product.qty === 0) {
      return true;
    }

    return false;
  }, [product]);

  const handleAddToCart = async () => {
    try {
      setLoading(true);

      if (product.available_qty === 0) {
        toast({
          title: 'Error',
          description: 'Product is out of stock',
          variant: 'destructive',
        });
        return;
      }

      const productResponse = await mutateProduct.mutateAsync(product.slug as string);
      const productDetail = productResponse.data;
      const productVariants = productDetail?.variants;

      const selectedItem = productVariants ? cartProduct : productDetail?.product_items?.[0];

      if (!selectedItem) {
        toast({
          title: 'Error',
          description: 'No product item available',
          variant: 'destructive',
        });
        return;
      }

      const {
        item_id: id,
        sell_price,
        item_weight,
        item_height,
        item_width,
        item_length,
      } = selectedItem;

      const item = {
        id: id || '',
        product_name: product?.product_name,
        sell_price: Number(sell_price),
        product_assets: product?.product_assets,
        quantity: cartProduct.quantity,
        variants: cartProduct.variants,
        sku: cartProduct.sku,
        item_weight: Number(item_weight),
        item_height: Number(item_height),
        item_width: Number(item_width),
        item_length: Number(item_length),
        total_value: Number(sell_price) * cartProduct.quantity,
      };

      const payload = {
        id: id || '',
        quantity: 1,
      };

      await addToCart(host, payload, customerToken);

      toast({
        duration: 1000,
        title: 'Success',
        description: 'Product has been added to cart',
        variant: 'success',
      });

      setCartPayload(item);
      setTimeout(() => popupDrawer.open(), 1000);

      queryClient.invalidateQueries({ queryKey: ['carts'] });

      SendEventAnalytic('add_to_cart', {
        currency: 'IDR',
        value: Number(item.sell_price) * item.quantity,
        items: [
          {
            item_name: item?.product_name,
            item_id: item.id as string,
            price: Number(item?.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });

      SendEventMetapixel('AddToCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_category: product.category_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(item.sell_price),
        currency: 'IDR',
      });
    } catch (error) {
      let message = 'Failed to add product to cart';
      if (error instanceof AxiosError) {
        message = error.response?.data?.message || message;
      }
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const { displayPrice, usingPricebookPrice } = useMemo(() => {
    const pricebook = product.pricebook || [];

    const defaultPrice = product.discount_price ? product.discount_price : product.sell_price || 0;

    if (!pricebook || pricebook.length === 0) {
      return {
        displayPrice: defaultPrice,
        usingPricebookPrice: false,
      };
    }

    const minUnit1Items = pricebook.filter((item) => item.min_unit === 1);

    if (minUnit1Items.length === 0) {
      return {
        displayPrice: defaultPrice,
        usingPricebookPrice: false,
      };
    }

    let pricebookPrice;

    if (minUnit1Items.length >= 2) {
      const validPrices = minUnit1Items
        .map((item) => item.retail_price)
        .filter((price) => price !== undefined && price !== null);

      if (validPrices.length === 0) {
        return {
          displayPrice: defaultPrice,
          usingPricebookPrice: false,
        };
      }

      pricebookPrice = Math.min(...validPrices);
    } else {
      pricebookPrice = minUnit1Items[0].retail_price;

      if (pricebookPrice === undefined || pricebookPrice === null) {
        return {
          displayPrice: defaultPrice,
          usingPricebookPrice: false,
        };
      }
    }

    const finalPrice = Math.min(pricebookPrice, defaultPrice);

    const usingPricebook = finalPrice === pricebookPrice && finalPrice < defaultPrice;

    return {
      displayPrice: finalPrice,
      usingPricebookPrice: usingPricebook,
    };
  }, [product]);

  const BADGES_PRODUCT = [
    product?.best_seller && {
      text: themeSetting?.badge?.bestSellerText || 'Best Seller',
      className:
        themeSetting?.badge?.position === 'inside'
          ? 'theme_badge_best_seller_gradient'
          : 'theme_badge_best_seller',
    },
    product?.new_arrival && {
      text: themeSetting?.badge?.newArrivalText || 'New Arrival',
      className:
        themeSetting?.badge?.position === 'inside'
          ? 'theme_badge_new_arrival_gradient'
          : 'theme_badge_new_arrival',
    },
    discountPercentage > 0 &&
      !usingPricebookPrice && {
        text: `${themeSetting?.badge?.promotionText || 'Discount'} ${discountPercentage.toFixed(
          0
        )}%`,
        className:
          themeSetting?.badge?.position === 'inside'
            ? 'theme_badge_promotion_gradient'
            : 'theme_badge_promotion',
      },
  ].filter(Boolean) as { text: string; className: string }[];

  const pricebook = product.pricebook || [];
  const isAnyPricebook = pricebook.length > 0;

  return (
    <>
      <div
        key={product.id}
        data-animation='true'
        data-animation-group={`productlist-item-${element.id}`}
        className='h-full'
      >
        <div
          className={cn(
            'card-rounded theme_animation_card_hover relative flex h-full flex-col items-stretch justify-start overflow-hidden rounded-none',
            themeSetting?.productCard?.style === 'card' ? 'theme_product_card' : 'data-color-scheme'
          )}
        >
          <div>
            {data.showImage && (
              <Link
                href={`/products/${product.slug}${
                  token
                    ? `?theme_id=${themeId}&token=${token}`
                    : preview
                    ? `?theme_id=${themeId}&isPreview=true`
                    : ''
                }`}
              >
                <div
                  className={cn('image_padding group relative w-full overflow-hidden !bg-white')}
                >
                  {data.showSecondImageOnHover && product.product_assets.length > 1 ? (
                    <div
                      className={cn(
                        'relative w-full overflow-hidden duration-300 hover:scale-[1.01] hover:shadow-lg',
                        data.imageRatio === 'adaptToImage' && 'img_ratio_adapt',
                        data.imageRatio === 'portrait' && 'img_ratio_portrait',
                        data.imageRatio === 'square' && 'img_ratio_square'
                      )}
                      style={{
                        backgroundColor:
                          data.colorScheme?.imageBackground ||
                          DEFAULT_THEME_COLOR_SCHEME.imageBackground,
                      }}
                    >
                      <DynamicImage
                        key={`${data.imageRatio}-first`}
                        className={cn(
                          'h-full w-full scale-100 bg-transparent object-cover duration-500 group-hover:scale-110 group-hover:opacity-0',
                          isSoldOut && 'grayscale'
                        )}
                        src={firstImage as unknown as string}
                        alt={product.product_name}
                        width={100}
                        height={100}
                        sizes='100vw'
                      />
                      <DynamicImage
                        key={`${data.imageRatio}-second`}
                        className={cn(
                          'absolute inset-0 h-full w-full scale-100 bg-transparent object-cover opacity-0 duration-500 group-hover:scale-110 group-hover:opacity-100',
                          isSoldOut && 'grayscale'
                        )}
                        src={secondImage as unknown as string}
                        alt={product.product_name}
                        width={100}
                        height={100}
                        sizes='100vw'
                      />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'relative w-full overflow-hidden duration-300 hover:scale-[1.01] hover:shadow-lg',
                        data.imageRatio === 'adaptToImage' && 'img_ratio_adapt',
                        data.imageRatio === 'portrait' && 'img_ratio_portrait',
                        data.imageRatio === 'square' && 'img_ratio_square'
                      )}
                      style={{
                        backgroundColor:
                          data.colorScheme?.imageBackground ||
                          DEFAULT_THEME_COLOR_SCHEME.imageBackground,
                      }}
                    >
                      <DynamicImage
                        key={data.imageRatio}
                        className={cn(
                          'h-full w-full bg-transparent object-cover transition-all duration-300 ease-in-out',
                          data.imageRatio === 'adaptToImage' && 'h-full',
                          isSoldOut ? 'grayscale' : 'scale-100'
                        )}
                        src={firstImage as unknown as string}
                        alt={(product.product_name as string) || 'Product image'}
                        width={100}
                        height={100}
                        sizes='100vw'
                      />
                    </div>
                  )}
                  {(themeSetting?.badge?.position === '' ||
                    themeSetting?.badge?.position === 'outside') &&
                    (isSoldOut ? (
                      <Badge className='theme_badge theme_badge_sold_out absolute font-normal'>
                        {themeSetting?.badge?.soldOutText || 'Sold Out'}
                      </Badge>
                    ) : (
                      <div className='theme_badge absolute gap-2'>
                        <div className='flex flex-1 flex-wrap gap-2'>
                          {BADGES_PRODUCT.map((badge, index) => (
                            <Badge
                              key={index}
                              className={cn('text-xs font-normal', badge.className)}
                            >
                              {badge.text}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  {data.enableQuickAddButton &&
                    themeSetting?.productCard?.buttonPosition === 'outside' && (
                      <div className='absolute inset-x-0 bottom-2 px-2'>
                        {product.has_variation === true ? (
                          <>
                            <Button
                              size='sm'
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                e.nativeEvent.stopImmediatePropagation();
                                onClickChooseOptions();
                              }}
                              className='theme_button button button_visibility mx-auto flex w-full items-center border'
                              style={{
                                backgroundColor:
                                  data.colorScheme?.btnSolidBackgroud ||
                                  themeSetting?.button?.colorScheme?.btnSolidBackgroud ||
                                  themeSetting?.productCard?.colorScheme?.btnSolidBackgroud,
                                color:
                                  data?.colorScheme?.btnSolidlabel ||
                                  themeSetting?.button?.colorScheme?.btnSolidlabel ||
                                  themeSetting?.productCard?.colorScheme?.btnSolidlabel,
                              }}
                              type='button'
                            >
                              {themeSetting?.productCard?.buttonText ||
                                DEFAULT_THEME_PRODUCT_CARD.buttonText}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              isLoading={isLoading}
                              size='sm'
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                e.nativeEvent.stopImmediatePropagation();
                                handleAddToCart();
                              }}
                              disabled={isSoldOut}
                              className='theme_button button button_visibility mx-auto flex w-full items-center border'
                              style={{
                                backgroundColor:
                                  data.colorScheme?.btnSolidBackgroud ||
                                  themeSetting?.button?.colorScheme?.btnSolidBackgroud ||
                                  themeSetting?.productCard?.colorScheme?.btnSolidBackgroud,
                                color:
                                  data?.colorScheme?.btnSolidlabel ||
                                  themeSetting?.button?.colorScheme?.btnSolidlabel ||
                                  themeSetting?.productCard?.colorScheme?.btnSolidlabel,
                              }}
                              type='button'
                            >
                              {themeSetting?.productDetailButton?.buttonLabelAddToCart ||
                                'Add to cart'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                </div>
              </Link>
            )}
          </div>
          <div className='flex h-full w-full flex-col justify-between gap-0.5 p-2'>
            <div className='flex-grow'>
              {themeSetting?.badge?.position === 'inside' &&
                (isSoldOut ? (
                  <div className='mb-1 w-full'>
                    <Badge
                      variant='custom'
                      className='theme_badge_inside w-full bg-[#616161] text-xs font-bold text-white'
                    >
                      {themeSetting?.badge?.soldOutText || 'Sold Out'}
                    </Badge>
                  </div>
                ) : (
                  <>
                    <div
                      className={cn(
                        'mb-1 grid w-full grid-cols-1  gap-1 lg:gap-2',
                        BADGES_PRODUCT.length === 1 ? 'md:grid-cols-1' : '',
                        BADGES_PRODUCT.length === 2 ? 'md:grid-cols-1 lg:grid-cols-2' : '',
                        BADGES_PRODUCT.length === 3 ? 'md:grid-cols-2 md:grid-rows-2' : ''
                      )}
                    >
                      {BADGES_PRODUCT.map((badge, index) => (
                        <Badge
                          key={index}
                          variant='custom'
                          className={cn(
                            'theme_badge_inside w-full text-[12px] font-bold lg:text-[14px]',
                            badge.className,
                            BADGES_PRODUCT.length === 3 && index === 2 ? 'col-span-2' : ''
                          )}
                        >
                          {badge.text}
                        </Badge>
                      ))}
                    </div>
                  </>
                ))}
              <Link
                href={`/products/${product.slug}${
                  token
                    ? `?theme_id=${themeId}&token=${token}`
                    : preview
                    ? `?theme_id=${themeId}&isPreview=true`
                    : ''
                }`}
                className='space-y-1'
              >
                {data.showCategory && (
                  <div className='text_content w-full'>
                    <span
                      className={cn(
                        'line-clamp-2 text-[10px] font-medium tracking-tight md:text-[12px]',
                        isSoldOut ? 'text-[#C2C2C2]' : 'text_product_secondary'
                      )}
                    >
                      {product.category_name}
                    </span>
                  </div>
                )}
                <div className='text_content w-full text-start'>
                  <h2
                    className={cn(
                      'line-clamp-2 text-[12px] tracking-tight md:text-[14px] lg:text-[16px]',
                      isSoldOut && 'text-[#C2C2C2]'
                    )}
                  >
                    {truncateText(
                      product.product_name,
                      themeSetting?.productCard?.limitCharacters as number
                    )}
                  </h2>
                </div>
              </Link>
              <div className='text_product_secondary text_content price_product text-[12px] font-bold md:text-[14px] lg:text-base'>
                <div className={cn(product.discount_price && 'flex flex-col items-start')}>
                  <div className='flex items-center justify-center gap-x-2'>
                    {isAnyPricebook && (
                      <DropdownMenu>
                        <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                          <PriceBookIcon
                            className='h-5 w-5'
                            color={themeSetting?.productCard?.colorScheme?.text}
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align='center'
                          className='z-[1000] w-[250px] rounded-none'
                        >
                          <DropdownMenuLabel className='text-sm'>Tiered Pricing</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuGroup className='max-h-[200px] overflow-y-auto rounded-none'>
                            {pricebook
                              .filter(
                                (item, index, self) =>
                                  self.findIndex(
                                    (t) =>
                                      t.min_unit === item.min_unit &&
                                      t.retail_price === item.retail_price
                                  ) === index
                              )
                              .sort((a, b) => (a.min_unit ?? 0) - (b.min_unit ?? 0))
                              .map((tier, index) => (
                                <DropdownMenuItem key={index} className='text-xs'>
                                  {`Buy ${
                                    tier.min_unit === 1 ? '1' : `> ${(tier?.min_unit ?? 0) - 1}`
                                  } for ${formatIDR(tier.retail_price ?? 0)}/unit`}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <span
                      className={cn(
                        'flex justify-between text-sm font-bold lg:text-base',
                        isSoldOut
                          ? 'text-[#C2C2C2]'
                          : discountPercentage > 0
                          ? 'text_product_secondary'
                          : 'text_product_primary'
                      )}
                    >
                      {formatIDR(displayPrice)}
                    </span>
                  </div>

                  {product.discount_price && !usingPricebookPrice ? (
                    <span className='text-sm font-normal leading-4 text-[#C2C2C2] line-through'>
                      {formatIDR(product.sell_price || 0)}
                    </span>
                  ) : null}
                </div>
              </div>
              {theme?.productCard?.showQtySold && (
                <div className='text_content mt-3 flex pb-2'>
                  <span
                    className={cn(
                      'text-[10px] lg:text-[12px]',
                      isSoldOut ? 'text-[#C2C2C2]' : 'text-[#616161]'
                    )}
                  >
                    {product.qty_sold !== 0 ? `${product.qty_sold}` : '0'}{' '}
                    {theme?.productCard?.qtySoldLabel || DEFAULT_THEME_PRODUCT_CARD.qtySoldLabel}
                  </span>
                </div>
              )}
            </div>
            {data.enableQuickAddButton && theme?.productCard?.buttonPosition !== 'outside' && (
              <div className={cn('relative py-4', theme?.productCard?.style === 'card' && 'px-1')}>
                {product.has_variation === true ? (
                  <>
                    <Button
                      onClick={onClickChooseOptions}
                      className={cn(
                        'theme_button mb-2 flex w-full items-center border text-xs',
                        !data.colorScheme && themeSetting?.productCard?.style === 'card' && 'button'
                      )}
                      style={{
                        backgroundColor:
                          data.colorScheme?.btnSolidBackgroud ||
                          themeSetting?.button?.colorScheme?.btnSolidBackgroud ||
                          themeSetting?.productCard?.colorScheme?.btnSolidBackgroud,
                        color:
                          data?.colorScheme?.btnSolidlabel ||
                          themeSetting?.button?.colorScheme?.btnSolidlabel ||
                          themeSetting?.productCard?.colorScheme?.btnSolidlabel,
                      }}
                      type='button'
                    >
                      {theme?.productCard?.buttonText || DEFAULT_THEME_PRODUCT_CARD.buttonText}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      isLoading={isLoading}
                      onClick={handleAddToCart}
                      disabled={isSoldOut}
                      className={cn(
                        'theme_button mb-2 flex w-full items-center border',
                        !data.colorScheme && theme?.productCard?.style === 'card' && 'button',
                        themeSetting?.productCard?.style === 'card' && 'px-4'
                      )}
                      style={{
                        backgroundColor:
                          data.colorScheme?.btnSolidBackgroud ||
                          themeSetting?.button?.colorScheme?.btnSolidBackgroud ||
                          themeSetting?.productCard?.colorScheme?.btnSolidBackgroud,
                        color:
                          data?.colorScheme?.btnSolidlabel ||
                          themeSetting?.button?.colorScheme?.btnSolidlabel ||
                          themeSetting?.productCard?.colorScheme?.btnSolidlabel,
                      }}
                      type='button'
                    >
                      {themeSetting?.productDetailButton?.buttonLabelAddToCart || 'Add to cart'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {themeSetting?.cart?.cartType === 'popup_notification' && popupDrawer.isShow ? (
        <CartPopup
          openModal={popupDrawer.isShow}
          cart={cartPayload}
          setOpenModal={popupDrawer.setIsShow}
          host={window.location.host}
        />
      ) : null}

      {productDetailModal.isShow && product && (
        <ModalProductDetail
          openModal={productDetailModal.isShow}
          setOpenModal={productDetailModal.setIsShow}
          preview={preview}
          themeId={themeId}
          storeTheme={theme}
          slug={product?.slug as string}
        />
      )}

      <style jsx>
        {`
          .theme_product_card {
            background-color: ${data.colorScheme?.background};
          }
          .button {
            background-color: ${data.colorScheme?.btnSolidBackgroud};
            color: ${data.colorScheme?.text} !important;
          }
          .data-color-scheme {
            background-color: ${data.colorScheme?.background};
            color: ${data?.colorScheme?.text};
            text-align: ${themeSetting?.productCard?.textAlignment};
            justify-content: ${themeSetting?.productCard?.textAlignment};
            align-items: ${themeSetting?.productCard?.textAlignment};
          }
          .img_ratio_adapt {
            height: auto !important;
            width: 100% !important;
          }
          .img_ratio_portrait {
            aspect-ratio: 3 / 4;
          }
          .img_ratio_square {
            aspect-ratio: 1 / 1;
          }
        `}
      </style>
    </>
  );
}
